import React, { useState, useEffect, useCallback } from 'react';
import {
  DndContext,
  rectIntersection,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { getCardImageUri } from '@mtg-deck-tool/shared';
import type { Card, CardInstance, GameZone, HordeZone } from '@mtg-deck-tool/shared';
import { cardDimensions, CARD_CORNER_RADIUS } from '../../lib/playtester-engine.js';
import { getBattlefieldEl } from './PlayerBattlefield.js';
import { getHordeBattlefieldEl } from './HordeBattlefield.js';
import { usePlaytesterStore } from '../../stores/playtester-store.js';
import { usePlaytesterKeyboard } from '../../hooks/usePlaytesterKeyboard.js';
import MobileNotice from './MobileNotice.js';
import PlaytesterTopBar from './PlaytesterTopBar.js';
import PlaytesterButtonColumn from './PlaytesterButtonColumn.js';
import PlaytesterBottomRow from './PlaytesterBottomRow.js';
import PlayerBattlefield from './PlayerBattlefield.js';
import ContextMenu from './ContextMenu.js';
import DiceResultToast from './DiceResultToast.js';
import LibraryPanel from './LibraryPanel.js';
import ZoneListPanel from './ZoneListPanel.js';
import ScryPanel from './ScryPanel.js';
import AddTokenPanel from './AddTokenPanel.js';
import CounterModal from './CounterModal.js';
import PTModal from './PTModal.js';
import CardHoverPreview from './CardHoverPreview.js';
import { HordeSetupScreen } from './HordeSetupScreen.js';
import { HordeArea } from './HordeArea.js';
import { HordePhaseIndicator } from './HordePhaseIndicator.js';
import { WinOverlay } from './WinOverlay.js';
import { LoseOverlay } from './LoseOverlay.js';

function isGameZone(value: string): value is GameZone {
  return ['library', 'hand', 'battlefield', 'graveyard', 'exile', 'command'].includes(value);
}

function isHordeDropZone(value: string): value is 'horde-library' | 'horde-battlefield' | 'horde-graveyard' {
  return value === 'horde-library' || value === 'horde-battlefield' || value === 'horde-graveyard';
}

function hordeZoneFromDropId(id: string): HordeZone | null {
  if (id === 'horde-library') return 'library';
  if (id === 'horde-battlefield') return 'battlefield';
  if (id === 'horde-graveyard') return 'graveyard';
  return null;
}

function resolveDropZone(overId: string): GameZone | null {
  if (isGameZone(overId)) return overId;
  return null;
}

export default function PlaytesterView() {
  const zoom = usePlaytesterStore((s) => s.zoom);
  const moveToZone = usePlaytesterStore((s) => s.moveToZone);
  const setHordeMoveResult = usePlaytesterStore((s) => s.setHordeMoveResult);
  const selectedInstanceIds = usePlaytesterStore((s) => s.selectedInstanceIds);
  const contextMenu = usePlaytesterStore((s) => s.contextMenu);
  const openPanel = usePlaytesterStore((s) => s.openPanel);
  const showCounterModal = usePlaytesterStore((s) => s.showCounterModal);
  const showPTModal = usePlaytesterStore((s) => s.showPTModal);
  const overlay = usePlaytesterStore((s) => s.overlay);
  const horde = usePlaytesterStore((s) => s.horde);

  // hoveredCard lives in the store so the keyboard handler (Shift+P) can read it
  const hoveredCard = usePlaytesterStore((s) => s.hoveredCard);
  const setHoveredCard = usePlaytesterStore((s) => s.setHoveredCard);
  const persistentPreviewCard = usePlaytesterStore((s) => s.persistentPreviewCard);
  const [showHordeSetup, setShowHordeSetup] = useState(false);
  // Fix #4: track the active dragged card so DragOverlay can show the card image
  const [activeDragInstance, setActiveDragInstance] = useState<CardInstance | null>(null);

  // Keyboard shortcuts
  usePlaytesterKeyboard();

  // Check viewport width for mobile notice
  const [isTooNarrow, setIsTooNarrow] = useState(window.innerWidth < 1024);
  useEffect(() => {
    const handler = () => setIsTooNarrow(window.innerWidth < 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // CSS custom properties for card size — scales linearly with zoom %
  const { cardW: cardWpx, cardH: cardHpx } = cardDimensions(zoom);
  const cardW = `${cardWpx}px`;
  const cardH = `${cardHpx}px`;

  function handleDragStart(event: DragStartEvent) {
    const data = event.active.data.current as
      | { instanceId?: string; zone?: GameZone; fromRevealPanel?: boolean; fromHordeBattlefield?: boolean; fromHordeGraveyard?: boolean; fromTokenPanel?: boolean; card?: Card }
      | undefined;

    // Token panel drag: build a temporary CardInstance for the overlay
    if (data?.fromTokenPanel && data.card) {
      const tempInstance: CardInstance = {
        instanceId: String(event.active.id),
        card: data.card,
        tapped: false,
        faceDown: false,
        doesntUntap: false,
        counters: {},
        castCount: 0,
        isToken: true,
        isCommander: false,
      };
      setActiveDragInstance(tempInstance);
      return;
    }

    if (!data?.instanceId) return;

    // Find the card instance across all player zones + horde zones
    const state = usePlaytesterStore.getState();
    const zones: (keyof typeof state)[] = ['hand', 'battlefield', 'graveyard', 'exile', 'command', 'library'];
    let found: CardInstance | null = null;
    for (const z of zones) {
      const arr = state[z] as CardInstance[];
      const inst = arr.find((i) => i.instanceId === data.instanceId);
      if (inst) { found = inst; break; }
    }
    if (!found && state.horde) {
      const hordeZones: ('library' | 'battlefield' | 'graveyard')[] = ['library', 'battlefield', 'graveyard'];
      for (const z of hordeZones) {
        const inst = state.horde[z].find((i) => i.instanceId === data.instanceId);
        if (inst) { found = inst; break; }
      }
      if (!found) {
        const inst = state.horde.revealPanel?.find((i) => i.instanceId === data.instanceId);
        if (inst) found = inst;
      }
    }
    setActiveDragInstance(found);
  }

  /** Compute absolute position within the player battlefield canvas from a drag event. */
  function computeBattlefieldPos(event: DragEndEvent): { x: number; y: number } | null {
    const rect = getBattlefieldEl()?.getBoundingClientRect();
    const translated = event.active.rect.current?.translated;
    if (!rect || !translated) return null;
    return {
      x: Math.max(0, Math.round(translated.left - rect.left)),
      y: Math.max(0, Math.round(translated.top - rect.top)),
    };
  }

  /** Compute absolute position within the horde battlefield canvas from a drag event. */
  function computeHordeBattlefieldPos(event: DragEndEvent): { x: number; y: number } | null {
    const rect = getHordeBattlefieldEl()?.getBoundingClientRect();
    const translated = event.active.rect.current?.translated;
    if (!rect || !translated) return null;
    return {
      x: Math.max(0, Math.round(translated.left - rect.left)),
      y: Math.max(0, Math.round(translated.top - rect.top)),
    };
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveDragInstance(null);
    const { active, over } = event;
    if (!over) return;

    const activeData = active.data.current as
      | { instanceId?: string; zone?: GameZone; fromRevealPanel?: boolean; fromHordeBattlefield?: boolean; fromHordeGraveyard?: boolean; fromTokenPanel?: boolean; card?: Card }
      | undefined;
    if (!activeData) return;

    const overId = String(over.id);

    // Cards dragged from the Add Token panel
    if (activeData.fromTokenPanel && activeData.card) {
      const store = usePlaytesterStore.getState();
      if (overId === 'horde-battlefield') {
        const pos = computeHordeBattlefieldPos(event);
        store.placeHordeToken(activeData.card, pos?.x, pos?.y);
      } else {
        // Default: place on player battlefield
        const pos = computeBattlefieldPos(event);
        store.placeToken(activeData.card, pos?.x, pos?.y);
        store.setOpenPanel(null);
      }
      return;
    }

    if (!activeData.instanceId) return;
    const instanceId = activeData.instanceId;

    // Cards dragged from the horde reveal panel
    if (activeData.fromRevealPanel) {
      const hordeZone = hordeZoneFromDropId(overId);
      if (hordeZone) {
        setHordeMoveResult(instanceId, hordeZone);
      }
      return;
    }

    // Cards dragged from horde battlefield to other horde zones
    if (activeData.fromHordeBattlefield) {
      const hordeZone = hordeZoneFromDropId(overId);
      if (hordeZone) {
        const { moveHordeCardToZone } = usePlaytesterStore.getState();
        moveHordeCardToZone(instanceId, 'battlefield', hordeZone);
      }
      return;
    }

    // Cards dragged from horde graveyard panel to other horde zones
    if (activeData.fromHordeGraveyard) {
      const hordeZone = hordeZoneFromDropId(overId);
      if (hordeZone) {
        const { moveHordeCardToZone } = usePlaytesterStore.getState();
        moveHordeCardToZone(instanceId, 'graveyard', hordeZone);
      }
      return;
    }

    // Regular player-side drag
    const fromZone = activeData.zone;
    if (!fromZone) return;

    const toZone = resolveDropZone(overId);
    if (!toZone) return;

    // Fix #5: drag-within-battlefield repositions the card (no zone change)
    if (fromZone === 'battlefield' && toZone === 'battlefield') {
      const pos = computeBattlefieldPos(event);
      if (pos) {
        const store = usePlaytesterStore.getState();
        const isMulti = selectedInstanceIds.size > 1 && selectedInstanceIds.has(instanceId);
        if (isMulti) {
          // Move all selected cards by the same delta as the dragged card
          const dragged = store.battlefield.find((i) => i.instanceId === instanceId);
          const dx = pos.x - (dragged?.x ?? 0);
          const dy = pos.y - (dragged?.y ?? 0);
          for (const id of selectedInstanceIds) {
            const inst = store.battlefield.find((i) => i.instanceId === id);
            if (inst) store.setCardPosition(id, Math.max(0, (inst.x ?? 0) + dx), Math.max(0, (inst.y ?? 0) + dy));
          }
        } else {
          store.setCardPosition(instanceId, pos.x, pos.y);
        }
      }
      return;
    }

    if (toZone === fromZone) return;

    // Multi-select: if the dragged card is part of a selection, move all selected
    if (selectedInstanceIds.size > 1 && selectedInstanceIds.has(instanceId)) {
      moveToZone(Array.from(selectedInstanceIds), fromZone, toZone);
    } else {
      moveToZone([instanceId], fromZone, toZone);
    }

    // Fix #5: after dropping a card onto battlefield, override position with exact drop coords
    if (toZone === 'battlefield') {
      const pos = computeBattlefieldPos(event);
      if (pos) {
        const isMulti = selectedInstanceIds.size > 1 && selectedInstanceIds.has(instanceId);
        const ids = isMulti ? Array.from(selectedInstanceIds) : [instanceId];
        // Use microtask to run after moveToZone state update completes
        queueMicrotask(() => {
          const store = usePlaytesterStore.getState();
          ids.forEach((id, i) => {
            store.setCardPosition(id, Math.max(0, pos.x + i * 4), Math.max(0, pos.y + i * 4));
          });
        });
      }
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-gray-950 flex flex-col overflow-hidden"
      style={
        {
          '--pt-card-w': cardW,
          '--pt-card-h': cardH,
        } as React.CSSProperties
      }
    >
      {/* Top Bar */}
      <PlaytesterTopBar onShowHordeSetup={() => setShowHordeSetup(true)} />

      {isTooNarrow ? (
        <MobileNotice />
      ) : (
        <DndContext collisionDetection={rectIntersection} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={() => setActiveDragInstance(null)}>
          {/* Board area: grows to fill space between top bar and bottom row */}
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
            {/* Horde area (above player battlefield, when horde is active).
                max-h is viewport-anchored so revealed cards always render at full
                card height even at high zoom. Player battlefield gets the rest. */}
            {horde && (
              <div className="shrink-0 overflow-y-auto border-b border-gray-700" style={{ maxHeight: '55vh' }}>
                <div className="p-2 flex flex-col gap-2">
                  <HordePhaseIndicator />
                  <HordeArea />
                </div>
              </div>
            )}

            {/* Player side: battlefield + button column */}
            <div className="flex-1 flex overflow-hidden min-h-0">
              <PlayerBattlefield />
              <PlaytesterButtonColumn />
            </div>
          </div>

          {/* Bottom row: hand + zone badges */}
          <PlaytesterBottomRow />

          {/* Drag overlay: card image for single drag, stacked ghost for multi */}
          <DragOverlay dropAnimation={null}>
            {activeDragInstance && selectedInstanceIds.size <= 1 ? (
              /* Fix #4: show actual card face following the cursor */
              <div
                // A tapped card must stay tapped while dragged — BattlefieldCard
                // rotates on `tapped`, and the overlay has to match or the card
                // snaps upright for the duration of the drag.
                className={`rounded-sm border border-gray-600 overflow-hidden shadow-2xl opacity-90 ${
                  activeDragInstance.tapped ? 'rotate-90' : ''
                }`}
                style={{ width: cardW, height: cardH }}
              >
                {(() => {
                  const artUri = activeDragInstance.faceDown
                    ? undefined
                    : getCardImageUri(activeDragInstance.card, 'large');
                  return artUri ? (
                    <img src={artUri} alt={activeDragInstance.card.name} className="w-full h-full object-cover" draggable={false} style={{ borderRadius: CARD_CORNER_RADIUS }} />
                  ) : (
                    <div className="w-full h-full bg-blue-950 flex items-center justify-center">
                      <div className="w-3/4 h-3/4 border-2 border-blue-700 rounded-sm opacity-60" />
                    </div>
                  );
                })()}
              </div>
            ) : activeDragInstance && selectedInstanceIds.size > 1 ? (
              <div className="relative" style={{ width: cardW, height: cardH }}>
                {Array.from(selectedInstanceIds)
                  .slice(0, 3)
                  .map((id, idx) => (
                    <div
                      key={id}
                      className="absolute bg-gray-700 border border-gray-600 rounded-sm"
                      style={{
                        width: cardW,
                        height: cardH,
                        top: `${idx * 3}px`,
                        left: `${idx * 3}px`,
                        opacity: 1 - idx * 0.2,
                      }}
                    />
                  ))}
                <div className="absolute bottom-1 right-1 bg-orange-600 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                  {selectedInstanceIds.size}
                </div>
              </div>
            ) : null}
          </DragOverlay>

          {/* Fix #9: panels inside DndContext so drag-out of panels works */}
          {openPanel === 'library' && <LibraryPanel />}
          {openPanel === 'graveyard' && <ZoneListPanel zone="graveyard" />}
          {openPanel === 'exile' && <ZoneListPanel zone="exile" />}
          {openPanel === 'token' && <AddTokenPanel />}
          {openPanel === 'scry' && <ScryPanel />}
        </DndContext>
      )}

      {/* Context menu (outside DndContext to avoid z-index issues) */}
      {contextMenu && <ContextMenu />}

      {/* Counter modal */}
      {showCounterModal && contextMenu && <CounterModal />}

      {/* P/T modal */}
      {showPTModal && contextMenu && <PTModal />}

      {/* Dice result toast */}
      <DiceResultToast />

      {/* Card hover preview */}
      <CardHoverPreview hoveredCard={persistentPreviewCard ?? hoveredCard} />

      {/* Phase 2: Win / Lose overlays */}
      {overlay === 'win' && <WinOverlay />}
      {overlay === 'lose' && <LoseOverlay />}

      {/* Phase 2: Horde setup screen */}
      {showHordeSetup && (
        <HordeSetupScreen onCancel={() => setShowHordeSetup(false)} />
      )}
    </div>
  );
}
